import { useRef, useMemo, useCallback } from 'react';
import { ButtonProps } from '@decisiv/ui-components/lib/components/Button';
import Pencil from '@decisiv/iconix/lib/components/Pencil';
import Modal from '@decisiv/ui-components/lib/components/Modal';
import { useNotifications } from '@decisiv/ui-components';
import { t } from '@lingui/macro';
import noop from 'lodash/noop';

import { createForm, FormHandler } from 'components/Form';
import { useFormContext } from 'react-hook-form';
import * as yup from 'yup';

import DepotForm from './DepotForm';
import { useUpdateDepotMutation } from './hooks';

import validationSchema from './validations';

type FormData = yup.InferType<typeof validationSchema>;

const { Form, FormProvider } = createForm<FormData>();

interface ContentProps {
  visible?: boolean;
  onClose: () => void;
  depotId: string;
}

const Content = ({ visible, onClose, depotId }: ContentProps) => {
  const {
    formState: { isValid },
    getValues,
    setError,
  } = useFormContext();

  const { notify } = useNotifications();

  const formRef = useRef<FormHandler<FormData>>(null);

  /* istanbul ignore next -- @preserve */
  const handleOnSubmit = useCallback(async () => {
    formRef.current?.submit();
  }, []);

  const clearFormAndClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const [updateDepot, { loading }] = useUpdateDepotMutation({
    onCompleted: (data) => {
      if (data?.updateAccountDepot?.depot) {
        notify({
          title: t`Depot updated successfully.`,
          intent: 'success',
          onClose: noop,
          duration: 5000,
        } as any);
        clearFormAndClose();
      } else if (data?.updateAccountDepot?.errors?.length) {
        const uniquenessError = data.updateAccountDepot.errors.find(
          (error: any) => error.field === 'dms_id' && error.code === 'taken',
        );

        if (uniquenessError) {
          setError('dmsId', {
            type: 'manual',
            message: t`Depot IDs must be unique across all teams.`,
          });
        } else {
          notify({
            title: t`Depot update failed due to an unexpected error. Try again or contact support if the issue persists.`,
            intent: 'danger',
          } as any);
          clearFormAndClose();
        }
      }
    },
    onError: /* istanbul ignore next -- @preserve */ (error: Error) => {
      notify({
        title: t`Failed to update depot: ${error.message}`,
        intent: 'danger',
      } as any);
    },
  });

  const handleSubmit = useCallback(async () => {
    const formData = getValues();
    await updateDepot({
      variables: {
        depotId,
        attributes: {
          dmsId: formData.dmsId,
          note: formData.note,
          name: formData.name,
          email: formData.email,
          address1: formData.address,
          city: formData.city,
          state: formData.state,
          postalCode: formData.postalCode,
          country: formData.country,
        },
      },
    });
  }, [getValues, updateDepot, depotId]);

  const actions = useMemo(() => {
    return [
      {
        disabled: !isValid || loading,
        text: t`Save`,
        loading,
        loadingText: t`Saving...`,
        onClick: handleSubmit,
      },
      { text: t`Cancel`, onClick: clearFormAndClose, disabled: loading },
    ] satisfies [ButtonProps, ButtonProps];
  }, [isValid, loading, handleSubmit, clearFormAndClose]);

  return (
    <Modal
      actions={actions}
      icon={Pencil}
      intent="information"
      onClose={clearFormAndClose}
      title={t`Edit Depot`}
      visible={visible}
    >
      <Form ref={formRef} onSubmit={handleOnSubmit}>
        <DepotForm />
      </Form>
    </Modal>
  );
};

interface UpdateDepotModalProps {
  visible?: boolean;
  handleClose: () => void;
  depot: {
    id: string;
    name: string;
    email?: string;
    address1?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    countryCode?: string;
    dmsId?: string;
    note?: string;
  };
}

export default function UpdateDepotModal({
  visible,
  handleClose,
  depot,
}: UpdateDepotModalProps) {
  const initialValues = useMemo(
    () => ({
      dmsId: depot.dmsId || '',
      note: depot.note || '',
      name: depot.name || '',
      email: depot.email || '',
      city: depot.city || '',
      state: depot.state || '',
      address: depot.address1 || '',
      postalCode: depot.postalCode || '',
      country: depot.countryCode || '',
    }),
    [depot],
  );

  return (
    <FormProvider
      key={depot.id}
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      <Content onClose={handleClose} visible={visible} depotId={depot.id} />
    </FormProvider>
  );
}
